import {
  type CreateRoomRequest,
  fetchRoomAddFacilitator,
  fetchRoomAddToRoom,
  fetchRoomChangeRoomOwnership,
  fetchRoomCreateRoom,
  fetchRoomDeleteFacilitator,
  fetchRoomDeleteRoom,
  fetchRoomDenyRequest,
  fetchRoomGetMyRooms,
  fetchRoomGetRoom,
  fetchRoomGetRoomPicture,
  fetchRoomRemoveFromRoom,
  fetchRoomRequestAddToRoom,
  fetchRoomUpdateRoom,
  fetchRoomUploadRoomPicture,
  type RoomDto,
  type RoomUploadRoomPictureRequestBody,
  type UpdateRoomRequest,
} from "@crowdcoursing/api/openapi";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { type Dispatch, type State } from "~hooks/useTypedRedux";
import { assertNonEmptyString } from "~utils/assert";

import { errorToast, successToast } from "../state/notifications";

const tDefaultErrorMessage = "Whoops! Sorry about that. Please try again.";

/** create a new room, uploading an image if one is selected
 * @deprecated We are phasing out `redux`
 */
export const createPod = createAsyncThunk(
  "myPods/createPod",
  async (
    {
      body,
      onSuccess,
    }: { body: Omit<CreateRoomRequest, "image"> & { image?: File }; onSuccess?: (room: RoomDto) => void },
    { dispatch },
  ) => {
    const room = await fetchRoomCreateRoom({ body });
    assertNonEmptyString(room?.id);
    if (body.image) {
      await dispatch(savePicture({ formFile: body.image, roomId: room.id }));
    }
    onSuccess?.(room);
    dispatch(successToast({ message: "Room created successfully!" }));
  },
);

/**
 * load a room from the backend and mark it as selected
 *
 * TODO: replace with graphql
 */
export const getSelectedPod = createAsyncThunk(
  "app/getPod",
  async (roomId: string) => await fetchRoomGetRoom({ pathParams: { roomId } }),
);

/** @deprecated We are phasing out `redux` */
export const getMyPods = createAsyncThunk("myPods/getMyPods", async (userId: string) => {
  return await fetchRoomGetMyRooms({ pathParams: { userId } });
});

// we listen for this in the app reducer to set the selected pod to undefined
/** @deprecated We are phasing out `redux` */
export const clearSelectedPod = createAsyncThunk("app/clearSelectedPod", async (userId: string) => {
  return getMyPods(userId);
});

// we listen for this in the app reducer and update the pod
/** @deprecated We are phasing out `redux` */
export const setSelectedPod = createAsyncThunk<RoomDto, string, { state: State; dispatch: Dispatch }>(
  "app/setSelectedPod",
  async (roomId: string, { getState, dispatch }) => {
    const userId = getState().user?.id;
    assertNonEmptyString(userId);
    await dispatch(getMyPods(userId));

    const pods: RoomDto[] = getState().pods.myPods;
    const pod = pods.find(({ id }) => id === roomId);
    if (!pod) {
      throw new Error(`(sessions)(updateSession) - unable to find pod with id (${roomId}) for user (${userId})`);
    }

    return pod;
  },
);

/** @deprecated We are phasing out `redux` */
export const updatePod = createAsyncThunk(
  "myPods/updatePod",
  async ({ body, onSuccess }: { body: UpdateRoomRequest; onSuccess?: () => void }, { dispatch, getState }) => {
    const userId = (getState() as State).user?.id;
    assertNonEmptyString(userId);
    assertNonEmptyString(body.roomId);
    await fetchRoomUpdateRoom({ body, pathParams: { roomId: body.roomId } });
    await dispatch(clearSelectedPod(userId));
    dispatch(successToast({ message: "Room updated successfully!" }));
    onSuccess?.();
  },
);

/** @deprecated We are phasing out `redux` */
export const requestMembership = createAsyncThunk(
  "app/requestMembershipPod",
  async (pathParams: { roomId: string; userId: string }, { dispatch }) => {
    try {
      await fetchRoomRequestAddToRoom({ pathParams });
      // updated request Ids are in this heavy data fetching endpoint
      const room = await fetchRoomGetRoom({ pathParams });
      if (room.isPublic) {
        dispatch(successToast({ message: "Join successful!" }));
      } else {
        dispatch(successToast({ message: "Join request sent!" }));
      }
      return room;
    } catch (error) {
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const addMember = createAsyncThunk(
  "app/addMemberPod",
  async (pathParams: { roomId: string; userId: string }, { dispatch }) => {
    try {
      await fetchRoomAddToRoom({ pathParams });
      dispatch(successToast({ message: "User added to room!" }));
    } catch (error) {
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const addFacilitator = createAsyncThunk(
  "app/addFacilitatorPod",
  async (pathParams: { roomId: string; userId: string }, { dispatch }) => {
    try {
      await fetchRoomAddFacilitator({ pathParams });
      dispatch(successToast({ message: "Facilitator added!" }));
    } catch (exception: unknown) {
      window.console.log("exception: ", JSON.stringify(exception, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const addOwner = createAsyncThunk<
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  void,
  { body: { roomId: string; userId: string }; onSuccess?: () => void },
  { state: State; dispatch: Dispatch }
>(
  "app/addOwnerPod",
  async (
    { body: pathParams, onSuccess }: { body: { roomId: string; userId: string }; onSuccess?: () => void },
    { dispatch },
  ) => {
    try {
      await fetchRoomChangeRoomOwnership({ pathParams });
      await dispatch(setSelectedPod(pathParams.roomId));
      dispatch(successToast({ message: "Ownership changed successfully!" }));
      onSuccess?.();
    } catch (exception: unknown) {
      window.console.log("exception: ", JSON.stringify(exception, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const removeFacilitator = createAsyncThunk(
  "app/removeFacilitatorPod",
  async (pathParams: { roomId: string; userId: string }, { dispatch }) => {
    try {
      await fetchRoomDeleteFacilitator({ pathParams });
      dispatch(successToast({ message: "Facilitator permissions removed successfully." }));
    } catch (exception: unknown) {
      window.console.log("exception: ", JSON.stringify(exception, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const rejectMember = createAsyncThunk(
  "app/rejectMemberPod",
  async (pathParams: { roomId: string; userId: string }, { dispatch }) => {
    try {
      await fetchRoomDenyRequest({ pathParams });
      dispatch(successToast({ message: "Member request rejected." }));
    } catch (error) {
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const deletePod = createAsyncThunk(
  "myPods/deletePod",
  async ({ roomId, onSuccess }: { roomId: string; onSuccess?: () => void }, { dispatch }) => {
    await fetchRoomDeleteRoom({ pathParams: { roomId } });
    dispatch(successToast({ message: "Room deleted successfully!" }));
    onSuccess?.();
    return roomId;
  },
);

/** @deprecated We are phasing out `redux` */
export const removeMember = createAsyncThunk(
  "app/removeMemberPod",
  async (pathParams: { roomId: string; userId: string }, { dispatch }) => {
    try {
      await fetchRoomRemoveFromRoom({ pathParams });
      // TODO: drop this re-fetch once my pods are converted to use graphql - this
      // only happens to try to keep our redux DB mirror in sync
      //
      // updated member Ids are in this heavy data fetching endpoint
      const room = fetchRoomGetRoom({ pathParams });
      dispatch(successToast({ message: "Member removed!" }));
      return room;
    } catch (error) {
      window.console.log("error: ", JSON.stringify(error, undefined, 2));
      dispatch(errorToast({ message: tDefaultErrorMessage }));
    }
  },
);

/** @deprecated We are phasing out `redux` */
export const savePicture = createAsyncThunk(
  "room/savePicture",
  async ({ formFile, ...pathParams }: { roomId: string; formFile: File }) => {
    // Thank goodness Ryan figured this out in `useResumeBuilder`
    const body = new FormData();
    body.append("FormFile", formFile);
    await fetchRoomUploadRoomPicture({ body: body as unknown as RoomUploadRoomPictureRequestBody, pathParams });
    return pathParams;
  },
);

/** @deprecated We are phasing out `redux` */
export const deletePicture = createAsyncThunk("room/deletePicture", async (roomId: string) => {
  await fetchRoomGetRoomPicture({ pathParams: { roomId } });
  return { roomId };
});
